/**
 * CurrencyContext — global FCFA / EUR / USD toggle for the diaspora.
 *
 * Rates are fetched from the backend (`GET /api/fx/rates`) which returns
 * `currency_per_fcfa`. The selected currency is persisted in localStorage.
 *
 * Usage:
 *   const { code, rate, format } = useCurrency();
 *   format(85000000) // → "85 000 000 FCFA" | "129 588 €" | "148 580 $"
 */
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const FX_URL = `${BACKEND_URL}/api/fx/rates`;
const STORAGE_KEY = 'realty237:currency';

const DEFAULT_RATES = {
  FCFA: 1,
  EUR: 1 / 655.957,  // fixed peg
  USD: 1 / 600,       // fallback (live rate fetched async)
};

const CURRENCY_META = {
  FCFA: { symbol: 'FCFA', label: 'FCFA', flag: '🇨🇲', decimals: 0 },
  EUR:  { symbol: '€',    label: 'Euro',  flag: '🇪🇺', decimals: 0 },
  USD:  { symbol: '$',    label: 'Dollar US', flag: '🇺🇸', decimals: 0 },
};

const CurrencyContext = createContext(null);

export const CurrencyProvider = ({ children }) => {
  const [code, setCode] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored && CURRENCY_META[stored] ? stored : 'FCFA';
    } catch { return 'FCFA'; }
  });
  const [rates, setRates] = useState(DEFAULT_RATES);
  const [usdSource, setUsdSource] = useState('fallback');

  // Fetch live rates once on mount; cache-friendly endpoint (1h s-maxage)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await axios.get(FX_URL, { timeout: 10000 });
        if (alive && data?.rates) {
          setRates({
            FCFA: 1,
            EUR: Number(data.rates.EUR) || DEFAULT_RATES.EUR,
            USD: Number(data.rates.USD) || DEFAULT_RATES.USD,
          });
          setUsdSource(data.usd_source || 'live');
        }
      } catch {
        // Keep defaults — degrade gracefully
      }
    })();
    return () => { alive = false; };
  }, []);

  // Persist selection
  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, code); } catch { /* ignore */ }
  }, [code]);

  const value = useMemo(() => {
    const rate = rates[code] ?? 1;
    const meta = CURRENCY_META[code] || CURRENCY_META.FCFA;
    const format = (fcfaAmount, opts = {}) => {
      if (fcfaAmount == null || Number.isNaN(Number(fcfaAmount))) return '—';
      const converted = Number(fcfaAmount) * rate;
      const rounded = Math.round(converted);
      const formatted = rounded.toLocaleString('fr-FR').replaceAll(',', ' ');
      if (opts.compact) {
        // For very large numbers, abbreviate (e.g., 85M FCFA → 85M FCFA / 130k €)
        const abs = Math.abs(converted);
        if (abs >= 1_000_000) return `${(converted / 1_000_000).toFixed(converted >= 10_000_000 ? 0 : 1)}M ${meta.symbol}`;
        if (abs >= 1_000) return `${Math.round(converted / 1_000)}k ${meta.symbol}`;
      }
      return code === 'FCFA' ? `${formatted} ${meta.symbol}` : `${formatted} ${meta.symbol}`;
    };
    return { code, setCode, rate, rates, meta, format, usdSource };
  }, [code, rates, usdSource]);

  return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
};

export const useCurrency = () => {
  const ctx = useContext(CurrencyContext);
  if (!ctx) {
    // Safety net — return a no-op formatter so non-wrapped trees still render
    return {
      code: 'FCFA',
      setCode: () => {},
      rate: 1,
      rates: DEFAULT_RATES,
      meta: CURRENCY_META.FCFA,
      usdSource: 'fallback',
      format: (n) => (n == null ? '—' : `${Math.round(n).toLocaleString('fr-FR').replaceAll(',', ' ')} FCFA`),
    };
  }
  return ctx;
};

export const CURRENCY_OPTIONS = Object.entries(CURRENCY_META).map(([code, m]) => ({ code, ...m }));
